/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { TokenTransferStatusEnum } from './TokenTransferStatusEnum';
import {
    TokenTransferStatusEnumFromJSON,
    TokenTransferStatusEnumFromJSONTyped,
    TokenTransferStatusEnumToJSON,
} from './TokenTransferStatusEnum';
import type { AdminTokenTransferTypeEnum } from './AdminTokenTransferTypeEnum';
import {
    AdminTokenTransferTypeEnumFromJSON,
    AdminTokenTransferTypeEnumFromJSONTyped,
    AdminTokenTransferTypeEnumToJSON,
} from './AdminTokenTransferTypeEnum';
import type { InvestmentAcquisitionTypeRecipientEnum } from './InvestmentAcquisitionTypeRecipientEnum';
import {
    InvestmentAcquisitionTypeRecipientEnumFromJSON,
    InvestmentAcquisitionTypeRecipientEnumFromJSONTyped,
    InvestmentAcquisitionTypeRecipientEnumToJSON,
} from './InvestmentAcquisitionTypeRecipientEnum';

/**
 * 
 * @export
 * @interface AdminTokenTransfer
 */
export interface AdminTokenTransfer {
    /**
     * 
     * @type {number}
     * @memberof AdminTokenTransfer
     */
    amount: number;
    /**
     * 
     * @type {Date}
     * @memberof AdminTokenTransfer
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    readonly id: string;
    /**
     * 
     * @type {InvestmentAcquisitionTypeRecipientEnum}
     * @memberof AdminTokenTransfer
     */
    readonly investmentAcquisitionTypeRecipient: InvestmentAcquisitionTypeRecipientEnum | null;
    /**
     * 
     * @type {InvestmentAcquisitionTypeRecipientEnum}
     * @memberof AdminTokenTransfer
     */
    readonly investmentAcquisitionTypeSender: InvestmentAcquisitionTypeRecipientEnum | null;
    /**
     * 
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    readonly investmentIdRecipient: string | null;
    /**
     * 
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    readonly investmentIdSender: string | null;
    /**
     * 
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    readonly investorNameRecipient: string | null;
    /**
     * 
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    readonly investorNameSender: string | null;
    /**
     * This field is specifically written to be shown as-is in the on the Investment details page in the transactions column
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    recipientColumn: string;
    /**
     * This field is specifically written to be shown as-is in the on the Investment details page in the transactions column
     * @type {string}
     * @memberof AdminTokenTransfer
     */
    senderColumn: string;
    /**
     * 
     * @type {TokenTransferStatusEnum}
     * @memberof AdminTokenTransfer
     */
    readonly status: TokenTransferStatusEnum;
    /**
     * 
     * @type {AdminTokenTransferTypeEnum}
     * @memberof AdminTokenTransfer
     */
    readonly type: AdminTokenTransferTypeEnum;
}



/**
 * Check if a given object implements the AdminTokenTransfer interface.
 */
export function instanceOfAdminTokenTransfer(value: object): value is AdminTokenTransfer {
    if (!('amount' in value) || value['amount'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('investmentAcquisitionTypeRecipient' in value) || value['investmentAcquisitionTypeRecipient'] === undefined) return false;
    if (!('investmentAcquisitionTypeSender' in value) || value['investmentAcquisitionTypeSender'] === undefined) return false;
    if (!('investmentIdRecipient' in value) || value['investmentIdRecipient'] === undefined) return false;
    if (!('investmentIdSender' in value) || value['investmentIdSender'] === undefined) return false;
    if (!('investorNameRecipient' in value) || value['investorNameRecipient'] === undefined) return false;
    if (!('investorNameSender' in value) || value['investorNameSender'] === undefined) return false;
    if (!('recipientColumn' in value) || value['recipientColumn'] === undefined) return false;
    if (!('senderColumn' in value) || value['senderColumn'] === undefined) return false;
    if (!('status' in value) || value['status'] === undefined) return false;
    if (!('type' in value) || value['type'] === undefined) return false;
    return true;
}

export function AdminTokenTransferFromJSON(json: any): AdminTokenTransfer {
    return AdminTokenTransferFromJSONTyped(json, false);
}

export function AdminTokenTransferFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminTokenTransfer {
    if (json == null) {
        return json;
    }
    return {
        
        'amount': json['amount'],
        'createdAt': (new Date(json['created_at'])),
        'id': json['id'],
        'investmentAcquisitionTypeRecipient': InvestmentAcquisitionTypeRecipientEnumFromJSON(json['investment_acquisition_type_recipient']),
        'investmentAcquisitionTypeSender': InvestmentAcquisitionTypeRecipientEnumFromJSON(json['investment_acquisition_type_sender']),
        'investmentIdRecipient': json['investment_id_recipient'],
        'investmentIdSender': json['investment_id_sender'],
        'investorNameRecipient': json['investor_name_recipient'],
        'investorNameSender': json['investor_name_sender'],
        'recipientColumn': json['recipient_column'],
        'senderColumn': json['sender_column'],
        'status': TokenTransferStatusEnumFromJSON(json['status']),
        'type': AdminTokenTransferTypeEnumFromJSON(json['type']),
    };
}

export function AdminTokenTransferToJSON(value?: Omit<AdminTokenTransfer, 'created_at'|'id'|'investment_acquisition_type_recipient'|'investment_acquisition_type_sender'|'investment_id_recipient'|'investment_id_sender'|'investor_name_recipient'|'investor_name_sender'|'status'|'type'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'amount': value['amount'],
        'recipient_column': value['recipientColumn'],
        'sender_column': value['senderColumn'],
    };
}

